/**
 * PDF Generator Utility
 * 
 * This utility provides functions to generate PDF reports from various data sources
 * including order history, inventory, and sales.
 */

interface PdfGeneratorOptions {
  title: string;
  subtitle?: string;
  dateRange?: {
    start: Date;
    end: Date;
  };
  companyName?: string;
  isRTL?: boolean;
  translate?: (key: string) => string;
}

/**
 * Formats a price for display
 */
export const formatPdfPrice = (value: number | string | undefined): string => {
  if (value === undefined) return "0";
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  return !isNaN(numericValue) ? numericValue.toLocaleString() : "0";
};

/**
 * Formats a date for display in PDFs
 */
export const formatPdfDate = (dateString: string | Date): string => {
  if (!dateString) return "-";
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return date.toLocaleDateString();
  } catch (e) {
    return "Invalid date";
  }
};

/**
 * Generates an order history PDF report
 */
export const generateOrderHistoryPDF = async (
  orders: any[],
  metrics: { totalOrders: number; totalItems: number; totalInvested: number },
  options: PdfGeneratorOptions
) => {
  // Dynamically import jsPDF to reduce initial bundle size
  const { default: jsPDF } = await import('jspdf');
  
  // Define translator function - use the provided one or fallback to identity function
  const t = options.translate || ((key: string) => key);
  
  // Determine if this is an RTL language
  const isRTL = options.isRTL || false;

  // Create a new PDF document
  const doc = new jsPDF('p', 'mm', 'a4');
  
  try {
    // Try to load the Arabic font extension if RTL
    if (isRTL) {
      // No need to import polyfills, jsPDF handles this automatically 
      // We would normally add additional font support here for RTL languages
      
      // This is pseudo-code, as we need to provide actual Arabic fonts
      // In a production app, you would include proper RTL font support
      // doc.addFont('path/to/arabic-font.ttf', 'arabic', 'normal');
      doc.addFont('public/fonts/Mehr_Nastaliq_Web v.2.0.woff', 'meher', 'normal');
      doc.setFont('meher');
    }
  } catch (e) {
    console.error("Failed to load RTL fonts:", e);
    // Fallback to standard font
  }
  
  // Add header
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  const title = t(options.title || 'Bookstore Report');
  doc.text(title, isRTL ? 190 - 105 : 105, 15, { align: isRTL ? 'right' : 'center' });
  
  // Add subtitle (usually date range)
  let subtitle = '';
  if (options.subtitle) {
    subtitle = t(options.subtitle);
  } else if (options.dateRange) {
    subtitle = `${t('Period')}: ${formatPdfDate(options.dateRange.start)} ${t('to')} ${formatPdfDate(options.dateRange.end)}`;
  }
  
  if (subtitle) {
    doc.setFontSize(12);
    doc.text(subtitle, isRTL ? 190 - 105 : 105, 25, { align: isRTL ? 'right' : 'center' });
  }
  
  // Add company info
  doc.setFontSize(10);
  const companyName = t(options.companyName || 'Naqeer BookStore Management');
  const generatedOn = `${t('Generated on')}: ${new Date().toLocaleDateString()}`;
  
  // Adjust X positions for RTL
  const companyX = isRTL ? 190 - 20 : 20;
  const dateX = isRTL ? 190 - 20 : 20;
  
  doc.text(companyName, companyX, 35, { align: isRTL ? 'right' : 'left' });
  doc.text(generatedOn, dateX, 40, { align: isRTL ? 'right' : 'left' });
  
  // Create a summary table at the top
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(240, 240, 240);
  doc.rect(20, 45, 170, 20, 'FD'); // Fill and draw a rectangle
  
  // Add summary headers
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  
  const totalOrdersText = t('Total Orders');
  const itemsPurchasedText = t('Items Purchased');
  const totalInvestmentText = t('Total Investment');
  
  // Adjust X positions for RTL
  const header1X = isRTL ? 190 - 30 : 30;
  const header2X = isRTL ? 190 - 80 : 80;
  const header3X = isRTL ? 190 - 140 : 140;
  
  doc.text(totalOrdersText, header1X, 53, { align: isRTL ? 'right' : 'left' });
  doc.text(itemsPurchasedText, header2X, 53, { align: isRTL ? 'right' : 'left' });
  doc.text(totalInvestmentText, header3X, 53, { align: isRTL ? 'right' : 'left' });
  
  // Add summary values
  doc.setFont('helvetica', 'normal');
  doc.text(String(metrics.totalOrders), header1X, 60, { align: isRTL ? 'right' : 'left' });
  doc.text(String(metrics.totalItems), header2X, 60, { align: isRTL ? 'right' : 'left' });
  doc.text(formatPdfPrice(metrics.totalInvested), header3X, 60, { align: isRTL ? 'right' : 'left' });
  
  // Add table header
  doc.setFillColor(230, 230, 230);
  doc.rect(20, 70, 170, 10, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  
  const col1 = t('Book Name');
  const col2 = t('Status');
  const col3 = t('Quantity');
  const col4 = t('Price/Book');
  const col5 = t('Total Cost');
  const col6 = t('Date');
  
  let colX1, colX2, colX3, colX4, colX5, colX6;
  
  if (!isRTL) {
    // LTR column positions
    colX1 = 25;
    colX2 = 75;
    colX3 = 95;
    colX4 = 115;
    colX5 = 140;
    colX6 = 165;
  } else {
    // RTL column positions (reverse order)
    colX6 = 25;
    colX5 = 50;
    colX4 = 75;
    colX3 = 100;
    colX2 = 125;
    colX1 = 150;
  }
  
  doc.text(col1, colX1, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col2, colX2, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col3, colX3, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col4, colX4, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col5, colX5, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col6, colX6, 76, { align: isRTL ? 'right' : 'left' });
  
  // Add horizontal line after header
  doc.setLineWidth(0.2);
  doc.line(20, 80, 190, 80);
  
  // Add table content
  let yPos = 85;
  let currentPage = 1;
  
  // Process orders for PDF
  for (let i = 0; i < orders.length; i++) {
    const order = orders[i];
    
    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      
      // Add page header on the new page
      doc.setFontSize(10);
      const pageTitle = `${t(options.title || 'Bookstore Report')} - ${t('Page')} ${currentPage + 1}`;
      doc.text(pageTitle, isRTL ? 190 - 105 : 105, 15, { align: isRTL ? 'right' : 'center' });
      
      // Reset yPos for the new page
      yPos = 30;
      
      // Add table header on new page
      doc.setFillColor(230, 230, 230);
      doc.rect(20, yPos, 170, 10, 'F');
      doc.setFont('helvetica', 'bold');
      
      // Repeat header row for new page
      doc.text(col1, colX1, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col2, colX2, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col3, colX3, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col4, colX4, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col5, colX5, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col6, colX6, yPos + 6, { align: isRTL ? 'right' : 'left' });
      
      // Add line after header
      yPos += 10;
      doc.line(20, yPos, 190, yPos);
      
      yPos += 5;
      currentPage++;
    }
    
    // Add order data
    doc.setFont('helvetica', 'normal');
    
    // Truncate book name if too long
    const bookName = order.book_name.length > 28 
      ? order.book_name.substring(0, 25) + '...' 
      : order.book_name;
    
    // Translate status
    const status = t(order.status);
      
    doc.text(bookName, colX1, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(status, colX2, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(String(order.quantity), colX3, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(order.price_per_book ? formatPdfPrice(order.price_per_book) : "-", colX4, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(order.total_cost ? formatPdfPrice(order.total_cost) : "-", colX5, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(formatPdfDate(order.created_at), colX6, yPos, { align: isRTL ? 'right' : 'left' });
    
    // Add a light line after each row
    yPos += 5;
    doc.setDrawColor(220, 220, 220);
    doc.line(20, yPos, 190, yPos);
    
    yPos += 5;
  }
  
  // Add footer
  const footerText = t('Report generated by Naqeer BookStore Management System');
  doc.setFontSize(8);
  doc.text(footerText, isRTL ? 190 - 105 : 105, 285, { align: isRTL ? 'right' : 'center' });
  
  // Return document for further processing
  return doc;
};

/**
 * Generates an inventory report PDF
 */
export const generateInventoryPDF = async (
  inventoryItems: any[],
  metrics: { totalItems: number; totalValue: number; lowStockItems: number },
  options: PdfGeneratorOptions
) => {
  // Dynamically import jsPDF to reduce initial bundle size
  const { default: jsPDF } = await import('jspdf');
  
  // Define translator function - use the provided one or fallback to identity function
  const t = options.translate || ((key: string) => key);
  
  // Determine if this is an RTL language
  const isRTL = options.isRTL || false;

  try {
    // Try to load the Arabic font extension if RTL
    if (isRTL) {
      // No need to import polyfills, jsPDF handles this automatically 
      // We would normally add additional font support here for RTL languages
      
      // This is pseudo-code, as we need to provide actual Arabic fonts
      // In a production app, you would include proper RTL font support
      // doc.addFont('path/to/arabic-font.ttf', 'arabic', 'normal');
      // doc.setFont('arabic');
    }
  } catch (e) {
    console.error("Failed to load RTL fonts:", e);
    // Fallback to standard font
  }
  
  // Create a new PDF document
  const doc = new jsPDF('p', 'mm', 'a4');
  
  // Add header
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  const title = t(options.title || 'Inventory Report');
  doc.text(title, isRTL ? 190 - 105 : 105, 15, { align: isRTL ? 'right' : 'center' });
  
  // Add subtitle (usually date range)
  let subtitle = '';
  if (options.subtitle) {
    subtitle = t(options.subtitle);
  } else if (options.dateRange) {
    subtitle = `${t('Period')}: ${formatPdfDate(options.dateRange.start)} ${t('to')} ${formatPdfDate(options.dateRange.end)}`;
  }
  
  if (subtitle) {
    doc.setFontSize(12);
    doc.text(subtitle, isRTL ? 190 - 105 : 105, 25, { align: isRTL ? 'right' : 'center' });
  }
  
  // Add company info
  doc.setFontSize(10);
  const companyName = t(options.companyName || 'Naqeer BookStore Management');
  const generatedOn = `${t('Generated on')}: ${new Date().toLocaleDateString()}`;
  
  // Adjust X positions for RTL
  const companyX = isRTL ? 190 - 20 : 20;
  const dateX = isRTL ? 190 - 20 : 20;
  
  doc.text(companyName, companyX, 35, { align: isRTL ? 'right' : 'left' });
  doc.text(generatedOn, dateX, 40, { align: isRTL ? 'right' : 'left' });
  
  // Create a summary table at the top
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(240, 240, 240);
  doc.rect(20, 45, 170, 20, 'FD'); // Fill and draw a rectangle
  
  // Add summary headers
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  
  const totalItemsText = t('Total Items');
  const totalValueText = t('Total Value');
  const lowStockItemsText = t('Low Stock Items');
  
  // Adjust X positions for RTL
  const header1X = isRTL ? 190 - 30 : 30;
  const header2X = isRTL ? 190 - 80 : 80;
  const header3X = isRTL ? 190 - 140 : 140;
  
  doc.text(totalItemsText, header1X, 53, { align: isRTL ? 'right' : 'left' });
  doc.text(totalValueText, header2X, 53, { align: isRTL ? 'right' : 'left' });
  doc.text(lowStockItemsText, header3X, 53, { align: isRTL ? 'right' : 'left' });
  
  // Add summary values
  doc.setFont('helvetica', 'normal');
  doc.text(String(metrics.totalItems), header1X, 60, { align: isRTL ? 'right' : 'left' });
  doc.text(formatPdfPrice(metrics.totalValue), header2X, 60, { align: isRTL ? 'right' : 'left' });
  doc.text(String(metrics.lowStockItems), header3X, 60, { align: isRTL ? 'right' : 'left' });
  
  // Add table header
  doc.setFillColor(230, 230, 230);
  doc.rect(20, 70, 170, 10, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  
  const col1 = t('Book Name');
  const col2 = t('Quantity');
  const col3 = t('Min Stock');
  const col4 = t('Price');
  const col5 = t('Value');
  const col6 = t('Last Updated');
  
  let colX1, colX2, colX3, colX4, colX5, colX6;
  
  if (!isRTL) {
    // LTR column positions
    colX1 = 25;
    colX2 = 75;
    colX3 = 95;
    colX4 = 115;
    colX5 = 135;
    colX6 = 160;
  } else {
    // RTL column positions (reverse order)
    colX6 = 25;
    colX5 = 45;
    colX4 = 65;
    colX3 = 85;
    colX2 = 105;
    colX1 = 125;
  }
  
  doc.text(col1, colX1, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col2, colX2, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col3, colX3, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col4, colX4, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col5, colX5, 76, { align: isRTL ? 'right' : 'left' });
  doc.text(col6, colX6, 76, { align: isRTL ? 'right' : 'left' });
  
  // Add horizontal line after header
  doc.setLineWidth(0.2);
  doc.line(20, 80, 190, 80);
  
  // Add table content
  let yPos = 85;
  let currentPage = 1;
  
  // Process inventory items for PDF
  for (let i = 0; i < inventoryItems.length; i++) {
    const item = inventoryItems[i];
    
    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      
      // Add page header on the new page
      doc.setFontSize(10);
      const pageTitle = `${t(options.title || 'Inventory Report')} - ${t('Page')} ${currentPage + 1}`;
      doc.text(pageTitle, isRTL ? 190 - 105 : 105, 15, { align: isRTL ? 'right' : 'center' });
      
      // Reset yPos for the new page
      yPos = 30;
      
      // Add table header on new page
      doc.setFillColor(230, 230, 230);
      doc.rect(20, yPos, 170, 10, 'F');
      doc.setFont('helvetica', 'bold');
      
      // Repeat header row for new page
      doc.text(col1, colX1, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col2, colX2, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col3, colX3, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col4, colX4, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col5, colX5, yPos + 6, { align: isRTL ? 'right' : 'left' });
      doc.text(col6, colX6, yPos + 6, { align: isRTL ? 'right' : 'left' });
      
      // Add line after header
      yPos += 10;
      doc.line(20, yPos, 190, yPos);
      
      yPos += 5;
      currentPage++;
    }
    
    // Add inventory item data
    doc.setFont('helvetica', 'normal');
    
    // Truncate book name if too long
    const bookName = item.book_name.length > 28 
      ? item.book_name.substring(0, 25) + '...' 
      : item.book_name;
      
    doc.text(bookName, colX1, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(String(item.quantity || 0), colX2, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(String(item.min_stocks || item.minStock || 0), colX3, yPos, { align: isRTL ? 'right' : 'left' });
    doc.text(item.price ? formatPdfPrice(item.price) : "-", colX4, yPos, { align: isRTL ? 'right' : 'left' });
    
    // Calculate and display total value (price * quantity)
    const value = item.price && item.quantity ? item.price * item.quantity : 0;
    doc.text(formatPdfPrice(value), colX5, yPos, { align: isRTL ? 'right' : 'left' });
    
    doc.text(formatPdfDate(item.updated_at || item.created_at), colX6, yPos, { align: isRTL ? 'right' : 'left' });
    
    // Add a light separator line
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(20, yPos + 3, 190, yPos + 3);
    
    yPos += 8; // Move down for the next row
  }
  
  // Add footer with page numbers
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  for (let i = 0; i < currentPage; i++) {
    doc.setPage(i);
    const pageFooter = `${t('Page')} ${i + 1} ${t('of')} ${currentPage}`;
    doc.text(pageFooter, isRTL ? 190 - 105 : 105, 290, { align: isRTL ? 'right' : 'center' });
  }
  
  // Generate filename
  let filename = 'inventory_report';
  if (options.dateRange) {
    const startStr = formatPdfDate(options.dateRange.start).replace(/\//g, '-');
    const endStr = formatPdfDate(options.dateRange.end).replace(/\//g, '-');
    filename = `inventory_report_${startStr}_to_${endStr}`;
  }
  
  // Save the PDF
  doc.save(`${filename}.pdf`);
  
  return true;
};

/**
 * Generates a sales report PDF
 */
export const generateSalesPDF = async (
  _sales: any[], // Not currently used, but kept for API consistency
  metrics: { totalSales: number; totalRevenue: number; totalProfit: number },
  options: PdfGeneratorOptions
) => {
  // Implementation will be similar to the other report types but with sales-specific fields
  // and with RTL support added
  const { default: jsPDF } = await import('jspdf');
  
  // Define translator function - use the provided one or fallback to identity function
  const t = options.translate || ((key: string) => key);
  
  // Determine if this is an RTL language
  const isRTL = options.isRTL || false;
  
  // Create a new PDF document
  const doc = new jsPDF('p', 'mm', 'a4');
  
  // Add header
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  const title = t(options.title || 'Sales Report');
  doc.text(title, isRTL ? 190 - 105 : 105, 15, { align: isRTL ? 'right' : 'center' });
  
  // Add subtitle (usually date range)
  let subtitle = '';
  if (options.subtitle) {
    subtitle = t(options.subtitle);
  } else if (options.dateRange) {
    subtitle = `${t('Period')}: ${formatPdfDate(options.dateRange.start)} ${t('to')} ${formatPdfDate(options.dateRange.end)}`;
  }
  
  if (subtitle) {
    doc.setFontSize(12);
    doc.text(subtitle, isRTL ? 190 - 105 : 105, 25, { align: isRTL ? 'right' : 'center' });
  }
  
  // Add company info
  doc.setFontSize(10);
  const companyName = t(options.companyName || 'Naqeer BookStore Management');
  const generatedOn = `${t('Generated on')}: ${new Date().toLocaleDateString()}`;
  
  // Adjust X positions for RTL
  const companyX = isRTL ? 190 - 20 : 20;
  const dateX = isRTL ? 190 - 20 : 20;
  
  doc.text(companyName, companyX, 35, { align: isRTL ? 'right' : 'left' });
  doc.text(generatedOn, dateX, 40, { align: isRTL ? 'right' : 'left' });
  
  // Create a summary table at the top
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(240, 240, 240);
  doc.rect(20, 45, 170, 20, 'FD'); // Fill and draw a rectangle
  
  // Add summary headers
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  
  const totalSalesText = t('Total Sales');
  const totalRevenueText = t('Total Revenue');
  const totalProfitText = t('Total Profit');
  
  // Adjust X positions for RTL
  const header1X = isRTL ? 190 - 30 : 30;
  const header2X = isRTL ? 190 - 80 : 80;
  const header3X = isRTL ? 190 - 140 : 140;
  
  doc.text(totalSalesText, header1X, 53, { align: isRTL ? 'right' : 'left' });
  doc.text(totalRevenueText, header2X, 53, { align: isRTL ? 'right' : 'left' });
  doc.text(totalProfitText, header3X, 53, { align: isRTL ? 'right' : 'left' });
  
  // Add summary values
  doc.setFont('helvetica', 'normal');
  doc.text(String(metrics.totalSales), header1X, 60, { align: isRTL ? 'right' : 'left' });
  doc.text(formatPdfPrice(metrics.totalRevenue), header2X, 60, { align: isRTL ? 'right' : 'left' });
  doc.text(formatPdfPrice(metrics.totalProfit), header3X, 60, { align: isRTL ? 'right' : 'left' });
  
  // Generate filename
  let filename = 'sales_report';
  if (options.dateRange) {
    const startStr = formatPdfDate(options.dateRange.start).replace(/\//g, '-');
    const endStr = formatPdfDate(options.dateRange.end).replace(/\//g, '-');
    filename = `sales_report_${startStr}_to_${endStr}`;
  }
  
  // Add footer with page numbers
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.text(`${t('Page')} 1 ${t('of')} 1`, isRTL ? 190 - 105 : 105, 290, { align: isRTL ? 'right' : 'center' });
  
  // Save the PDF
  doc.save(`${filename}.pdf`);
  
  return true;
}; 